import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { all_routes } from '../../../feature-module/router/all_routes';
import { useAuth } from '../../../context/AuthContext';
import { SearchResult } from '../../../types/types';
import { formatPhoneNumber } from '../../../utils/phone';
import {
  getParentAvatar,
  getCoachAvatar,
  getPlayerAvatar,
  resolveParentDefaultAvatar,
} from '../../../utils/avatarUtils';

interface SearchBarProps {
  role?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({ role }) => {
  const { searchAll, fetchGuardians, fetchPlayerData } = useAuth();
  const navigate = useNavigate();
  const routes = all_routes;

  // Search related state
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle click outside search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle search term changes
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const searchResults = await searchAll(searchTerm);
        setResults(searchResults);
        setShowResults(true);
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, searchAll]);

  // Helper function to get the correct avatar URL
  const getAvatarUrl = (result: SearchResult): string => {
    // If the backend returned a concrete avatar/URL, normalize legacy paths
    if (result.image) {
      if (result.image.includes('res.cloudinary.com')) {
        return `${result.image}${result.image.includes('?') ? '&' : '?'}${Date.now()}`;
      }
      if (result.image.startsWith('/uploads/')) {
        return `https://bothell-select.onrender.com${result.image}`;
      }
      // R2 or absolute URL
      if (result.image.startsWith('http')) return result.image;
    }

    // Fallback to local defaults based on type
    switch (result.type) {
      case 'player':
        return getPlayerAvatar(undefined, result.gender);

      // ✅ Agent wins over coach for parents & guardians
      case 'parent':
      case 'guardian':
        return resolveParentDefaultAvatar({
          isCoach: false,
          relationship: (result as any).relationship,
        });

      case 'coach':
        return resolveParentDefaultAvatar({
          isCoach: true,
          relationship: (result as any).relationship,
        });

      default:
        return getParentAvatar(undefined);
    }
  };

  // Enhanced function to fetch full player data with seasons
  const fetchPlayerWithSeasons = async (playerId: string) => {
    try {
      const fullPlayerData = await fetchPlayerData(playerId);
      if (fullPlayerData && (fullPlayerData.seasons || fullPlayerData.season)) {
        console.log('Full player data with seasons:', fullPlayerData);
        return fullPlayerData;
      }
      return null;
    } catch (error) {
      console.error('Error fetching player details:', error);
      return null;
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (results.length > 0) {
      handleResultClick(results[0]);
    }
  };

  const handleResetSearch = () => {
    if (results.length > 0) {
      handleResultClick(results[0]);
    } else {
      setSearchTerm('');
      setShowResults(false);
    }

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handlePlayerNavigation = useCallback(
    async (result: SearchResult) => {
      console.log('Navigating with player status:', result.status);

      try {
        const guardians = await fetchGuardians(result.id);

        // Fetch full player data including seasons
        const fullPlayerData = await fetchPlayerWithSeasons(result.id);

        // Use full player data if available, otherwise use search result data with enhanced seasons
        const playerData = fullPlayerData
          ? {
              ...fullPlayerData,
              _id: fullPlayerData._id || result.id,
              id: fullPlayerData.id || result.id,
              fullName: fullPlayerData.fullName || result.name,
              name: fullPlayerData.name || result.name,
              imgSrc:
                fullPlayerData.imgSrc || fullPlayerData.avatar || result.image,
              avatar: fullPlayerData.avatar || result.image,
              seasons: (fullPlayerData.seasons ||
                (fullPlayerData.season
                  ? [
                      {
                        season: fullPlayerData.season,
                        year:
                          fullPlayerData.registrationYear ||
                          new Date().getFullYear(),
                        registrationDate: fullPlayerData.createdAt,
                        paymentComplete:
                          fullPlayerData.paymentComplete ||
                          fullPlayerData.status === 'Active',
                      },
                    ]
                  : [])) as any,
            }
          : {
              _id: result.id,
              id: result.id,
              fullName: result.name,
              name: result.name,
              gender: result.gender || '',
              grade: result.grade || '',
              dob: result.dob,
              aauNumber: result.aauNumber || '',
              status: result.status || '',
              season: result.season || '',
              registrationYear:
                result.registrationYear || new Date().getFullYear(),
              schoolName: result.additionalInfo || '',
              section: result.additionalInfo || '',
              class: result.grade || '',
              createdAt: result.createdAt,
              playerStatus: result.status,
              imgSrc: result.image,
              avatar: result.image,
              seasons: (result.seasons || [
                {
                  season: result.season || 'Bothell Select',
                  year: result.registrationYear || new Date().getFullYear(),
                  tryoutId: result.season?.toLowerCase().includes('tryout')
                    ? `${result.season?.toLowerCase().replace(/\s+/g, '')}-${
                        result.registrationYear
                      }`
                    : `${
                        result.season?.toLowerCase().replace(/\s+/g, '') ||
                        'basketballselect'
                      }-${result.registrationYear}`,
                  registrationDate:
                    result.createdAt || new Date().toISOString(),
                  paymentComplete: result.status === 'Active',
                  amountPaid:
                    result.status === 'Active'
                      ? result.season?.includes('Tryout')
                        ? 20
                        : 1050
                      : undefined,
                  cardLast4:
                    result.status === 'Active'
                      ? result.season?.includes('Tryout')
                        ? '3234'
                        : '1111'
                      : undefined,
                  cardBrand: result.status === 'Active' ? 'VISA' : undefined,
                },
              ]) as any,
              registrationComplete: true,
            };

        console.log('Full player data being passed:', playerData);
        console.log('Player seasons data:', playerData.seasons);

        navigate(`${routes.playerDetail}/${result.id}`, {
          state: {
            player: playerData,
            guardians,
            fromSearch: true,
            key: Date.now(),
            timestamp: Date.now(),
          },
        });
      } catch (err) {
        console.error('Navigation error:', err);
        navigate(`${routes.playerDetail}/${result.id}`, {
          state: {
            player: {
              _id: result.id,
              id: result.id,
              fullName: result.name,
              name: result.name,
              status: result.status || 'inactive',
              imgSrc: result.image,
              avatar: result.image,
              seasons: [
                {
                  season: result.season || 'Bothell Select',
                  year: result.registrationYear || new Date().getFullYear(),
                  tryoutId: result.season?.toLowerCase().includes('tryout')
                    ? `${result.season?.toLowerCase().replace(/\s+/g, '')}-${
                        result.registrationYear
                      }`
                    : `${
                        result.season?.toLowerCase().replace(/\s+/g, '') ||
                        'basketballselect'
                      }-${result.registrationYear}`,
                  registrationDate:
                    result.createdAt || new Date().toISOString(),
                  paymentComplete: result.status === 'Active',
                  amountPaid:
                    result.status === 'Active'
                      ? result.season?.includes('Tryout')
                        ? 20
                        : 1050
                      : undefined,
                  cardLast4:
                    result.status === 'Active'
                      ? result.season?.includes('Tryout')
                        ? '3234'
                        : '1111'
                      : undefined,
                  cardBrand: result.status === 'Active' ? 'VISA' : undefined,
                },
              ] as any,
            },
            fromSearch: true,
            key: Date.now(),
          },
        });
      }
    },
    [fetchGuardians, navigate, routes.playerDetail, fetchPlayerData],
  );

  const handleSchoolNavigation = useCallback(
    (result: SearchResult) => {
      const basePath = routes.PlayerList || routes.playerList;
      const schoolPath = `${basePath}?school=${encodeURIComponent(result.name)}`;
      navigate(schoolPath, {
        state: { searchFilter: result.name, schoolFilter: result.name },
      });
    },
    [navigate, routes.PlayerList, routes.playerList],
  );

  const handleResultClick = useCallback(
    async (result: SearchResult) => {
      setShowResults(false);
      setSearchTerm('');

      try {
        switch (result.type) {
          case 'player':
            await handlePlayerNavigation(result);
            break;

          case 'parent':
          case 'guardian':
          case 'coach':
            navigate(`${routes.parentDetail}/${result.id}`, {
              state: {
                parent: {
                  _id: result.id,
                  fullName: result.name,
                  email: result.email,
                  phone: result.phone,
                  address: result.address,
                  relationship: (result as any).relationship,
                  isCoach: result.type === 'coach',
                  ...(result.isPaymentMatch &&
                    result.paymentDetails && {
                      paymentInfo: {
                        cardBrand: result.paymentDetails.cardBrand,
                        cardLastFour: result.paymentDetails.cardLastFour,
                        ...(result.paymentDetails.amount && {
                          amount: result.paymentDetails.amount,
                        }),
                        ...(result.paymentDetails.date && {
                          date: result.paymentDetails.date,
                        }),
                        ...(result.paymentDetails.receiptUrl && {
                          receiptUrl: result.paymentDetails.receiptUrl,
                        }),
                      },
                    }),
                },
                key: Date.now(),
              },
            });
            break;

          case 'school':
            await handleSchoolNavigation(result);
            break;

          default:
            console.warn('Unhandled search result type:', result.type);
        }
      } catch (error) {
        console.error('Navigation failed:', error);
        setShowResults(true);
      }
    },
    [
      handlePlayerNavigation,
      navigate,
      routes.parentDetail,
      handleSchoolNavigation,
    ],
  );

  const renderSearchResults = () => (
    <div className='search-results-dropdown'>
      {isLoading ? (
        <div className='search-result-item'>
          <div className='d-flex align-items-center justify-content-center'>
            <div
              className='spinner-border spinner-border-sm me-2'
              role='status'
            >
              <span className='visually-hidden'>Loading...</span>
            </div>
            <span>Searching...</span>
          </div>
        </div>
      ) : results.length > 0 ? (
        results.map((result: SearchResult) => {
          const avatarUrl = getAvatarUrl(result);
          return (
            <div
              key={`${result.type}-${result.id}`}
              className={`search-result-item cursor-pointer ${
                result.type === 'parent' ? 'parent-result' : ''
              } ${result.isPaymentMatch ? 'payment-match' : ''}`}
              onClick={() => handleResultClick(result)}
            >
              <div className='d-flex align-items-center'>
                <div className='avatar avatar-sm me-2'>
                  <img
                    src={avatarUrl}
                    alt={result.name || 'User avatar'}
                    className='avatar-img rounded-circle'
                    onError={(e) => {
                      // ✅ Same precedence rules via shared helper
                      const target = e.target as HTMLImageElement;
                      switch (result.type) {
                        case 'player':
                          target.src = getPlayerAvatar(
                            undefined,
                            result.gender,
                          );
                          break;
                        case 'coach':
                          target.src = resolveParentDefaultAvatar({
                            isCoach: true,
                            relationship: (result as any).relationship,
                          });
                          break;
                        case 'parent':
                        case 'guardian':
                          target.src = resolveParentDefaultAvatar({
                            isCoach: false,
                            relationship: (result as any).relationship,
                          });
                          break;
                        default:
                          target.src = getParentAvatar(undefined);
                      }
                    }}
                  />
                </div>
                <div className='flex-grow-1'>
                  <h6 className='mb-0'>
                    {result.name}
                    {result.type === 'parent' && (
                      <span className='badge bg-info ms-2'>Parent</span>
                    )}
                    {result.isPaymentMatch && (
                      <span className='badge bg-warning ms-2'>
                        Payment Match
                      </span>
                    )}
                  </h6>
                  {result.email && (
                    <small className='text-muted'>{result.email}</small>
                  )}
                  {result.phone && (
                    <small className='text-muted d-block'>
                      {formatPhoneNumber(result.phone)}
                    </small>
                  )}
                  {result.additionalInfo && (
                    <small className='d-block'>{result.additionalInfo}</small>
                  )}
                  {result.type === 'player' && result.season && (
                    <small className='d-block text-primary'>
                      {result.season} {result.registrationYear} •{' '}
                      {result.status}
                    </small>
                  )}
                  {result.type === 'player' &&
                    result.seasons &&
                    result.seasons.length > 0 && (
                      <small className='d-block text-success'>
                        {result.seasons.length} season
                        {result.seasons.length > 1 ? 's' : ''} registered
                      </small>
                    )}
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className='search-result-item'>
          <div className='text-center py-2'>No results found</div>
        </div>
      )}
    </div>
  );

  return (
    <div className='nav-item nav-search-inputs me-auto' ref={searchRef}>
      {role === 'admin' && (
        <div className='top-nav-search'>
          <button
            type='button'
            className='responsive-search'
            onClick={handleResetSearch}
          >
            <i className='fa fa-search' />
          </button>
          <form onSubmit={handleSearchSubmit} action='#' className='dropdown'>
            <div className='searchinputs' id='dropdownMenuClickable'>
              <input
                type='text'
                placeholder='Search players, parents, schools, phone, or last 4 of card...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => searchTerm && setShowResults(true)}
                ref={inputRef}
              />
              <div className='search-addon'>
                <button type='submit'>
                  <i className='ti ti-command' />
                </button>
              </div>
              {showResults &&
                (results.length > 0 || isLoading) &&
                renderSearchResults()}
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
